import { emailSchema } from '@/utils/schemas'
import { z } from 'zod'

export enum UserRole {
  Scheduler = 'scheduler',
  SuperAdmin = 'super_admin',
  Admin = 'admin',
  Secretary = 'secretary',
  Employee = 'employee',
  Client = 'client',
}

export const UserRoleFormatter: Record<UserRole, string> = {
  [UserRole.Scheduler]: 'Programador',
  [UserRole.SuperAdmin]: 'Super Admin',
  [UserRole.Admin]: 'Admin',
  [UserRole.Secretary]: 'Secretario',
  [UserRole.Employee]: 'Empleado',
  [UserRole.Client]: 'Cliente',
}

export const formatUserRole = (role: UserRole): string => {
  return UserRoleFormatter[role]
}

export const formatUserRoles = (roles: UserRole[]): string => {
  return roles.map((role) => formatUserRole(role)).join(', ')
}

export enum UserStatus {
  Active = 'active',
  Inactive = 'inactive',
  Pending = 'pending',
  Suspended = 'suspended',
}

export interface User {
  id: string
  email: string
  fullName?: string
  roles: UserRole[]
  status: UserStatus
  lastLogin?: string | null
  createdAt: string
  updatedAt: string
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface RegisterData {
  email: string
  password: string
  roles?: string[]
}

export interface ChangePasswordData {
  currentPassword: string
  newPassword: string
}

export interface CreateUserData {
  email: string
  password?: string
  roles?: UserRole[]
  fullName?: string
}

export interface UpdateUserData {
  email?: string
  status?: UserStatus
  roles?: UserRole[]
  fullName?: string
}

export interface TokenPair {
  accessToken: string
  refreshToken: string
}

export interface ApiResponse<T> {
  code: number
  message: string
  error?: string
  data?: T
}

export interface LoginResponseData {
  user: User
  tokens: TokenPair
}

export const hasRole = (user: User | null, role: UserRole): boolean => {
  return user?.roles?.includes(role) ?? false
}

export const hasAnyRole = (user: User | null, roles: UserRole[]): boolean => {
  return roles.some(role => hasRole(user, role))
}

export const isManagementUser = (user: User | null): boolean => {
  return hasAnyRole(user, [UserRole.Employee, UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler])
}

export const isPublicUser = (user: User | null): boolean => {
  return hasRole(user, UserRole.Client)
}

export const canAccessManagement = (user: User | null): boolean => {
  return isManagementUser(user)
}

export const canAccessPublic = (user: User | null): boolean => {
  return isPublicUser(user)
}

export const canAccessAdmin = (user: User | null): boolean => {
  return hasRole(user, UserRole.Admin) || hasRole(user, UserRole.SuperAdmin)
}

export interface ForgotPasswordData {
  email: string
}

export interface ResetPasswordData {
  token: string
  newPassword: string
}

// Zod validation schemas
export const loginCredentialsSchema = z.object({
  email: z.string().regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'El formato del email es inválido'),
  password: z.string().min(1, 'Password is required'),
})

export const registerDataSchema = z.object({
  email: emailSchema,
  password: z.string().min(8, 'Password must be at least 8 characters'),
  roles: z.array(z.string()).optional(),
})

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z.string().min(8, 'New password must be at least 8 characters'),
})

export const forgotPasswordSchema = z.object({
  email: z.string().regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'El formato del email es inválido'),
})

export const resetPasswordSchema = z.object({
  token: z.string().min(1, 'Reset token is required'),
  newPassword: z.string().min(8, 'Password must be at least 8 characters'),
})

